<template>
  <KioskLayout
    :classes="{
      container: `CustomerPortraitsDownloadCode${sessionPass ? ' CustomerPortraitsDownloadCode--themed' : ''}`,
      content: 'CustomerPortraitsDownloadCode__content',
    }"
    is-centered
  >

    <template #KioskLayout__top-right>
      <div v-if="!sessionPass">
        <a
          v-if="store.kiosk?.logo_url"
          class="CustomerPortraitsDownloadCode__partner"
          href="https://theartisan.ai/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            class="CustomerPortraitsDownloadCode__partner-image"
            :src="PARTNER_LOGO"
            alt="Powered by TheArtisan.ai"
          >
        </a>
      </div>
    </template>

    <CustomerDownloadStatusPanel
      v-if="!showsSessionPass"
      :status="pageStatus"
      @enter-code="enterAnotherCode"
      @retry="retryDownloadCodePage"
    />

    <CustomerDownloadLayout
      v-if="showsSessionPass"
      :key="getDownloadCodeFromRouter()"
      :session-pass="sessionPass"
      :has-extras="hasExtras"
    >
    <CustomerDownloadTabs
      class="CustomerPortraitsDownloadCode__gallery-content"
      :session-pass="sessionPass"
      :has-portraits="pageStatus === 'ready'"
      :has-extras="hasExtras"
    >
      <template #order><CustomerDigitalExtras v-bind="extrasView" receipt-only @retry="retryExtras" /></template>
      <template #extras>
        <CustomerDigitalExtras v-bind="extrasView" @retry="retryExtras" />
      </template>
      <CustomerDownloadStatusPanel
        v-if="hasPartialData"
        compact
        status="partial"
      />

      <CustomerPortraitDownloadGallery
        v-if="pageStatus === 'ready'"
        ref="gallery"
        :action-font-scale="customerDownloadActionFontScale"
        :greenscreen="isGreenscreen"
        :items="galleryItems"
        @download="downloadPortrait"
        @image-load="onPortraitLoad"
        @image-error="onPortraitLoadError"
        @retry="retryLoadingPortrait"
        @share="sendOrSharePortrait"
      />
    </CustomerDownloadTabs>
    </CustomerDownloadLayout>
  </KioskLayout>
</template>


<script>
import { shallowRef } from 'vue';
import { shortenLocaleCode } from '@/app/i18n/locale-matcher';
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import CustomerDigitalExtras from '@/components/customer/download-code/CustomerDigitalExtras.vue';
import CustomerDownloadLayout from '@/components/customer/download-code/CustomerDownloadLayout.vue';
import CustomerDownloadStatusPanel from '@/components/customer/download-code/CustomerDownloadStatusPanel.vue';
import CustomerDownloadTabs from '@/components/customer/download-code/CustomerDownloadTabs.vue';
import CustomerPortraitDownloadGallery from '@/components/customer/download-code/CustomerPortraitDownloadGallery.vue';
import { useCustomerDigitalExtras } from '@/components/customer/download-code/useCustomerDigitalExtras';
import { useCustomerDownloadCodePage } from '@/components/customer/download-code/useCustomerDownloadCodePage';
import { useCustomerPortraitDelivery } from '@/components/customer/download-code/useCustomerPortraitDelivery';
import { useCustomerPortraitGalleryState } from '@/components/customer/download-code/useCustomerPortraitGalleryState';
import { useCustomerDownloadDocumentLayout } from '@/components/customer/useCustomerDownloadDocumentLayout';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import PARTNER_LOGO from '../../assets/images/by-theartisan-citysaurus.svg';

const COMPACT_ACTION_LANGUAGES = new Set(['de', 'nl', 'es', 'pt', 'fr']);

export default {
  setup() {
    useCustomerDownloadDocumentLayout();
    const isUnmountedState = shallowRef(false);
    const loadStartTimeState = shallowRef(null);
    const galleryState = useCustomerPortraitGalleryState({
      isUnmounted: isUnmountedState,
      loadStartTime: loadStartTimeState,
    });
    const digitalExtras = useCustomerDigitalExtras();
    const page = useCustomerDownloadCodePage({
      digitalExtras,
      galleryState,
      isUnmounted: isUnmountedState,
      loadStartTime: loadStartTimeState,
    });

    return {
      brandingWarning: page.brandingWarning,
      extrasView: digitalExtras.view,
      galleryItems: galleryState.galleryItems,
      getDownloadAbortSignal: page.getAbortSignal,
      getDownloadCodeFromRouter: page.getDownloadCodeFromRouter,
      hasExtras: digitalExtras.hasExtras,
      initializePortraits: galleryState.initializePortraits,
      isGreenscreen: page.isGreenscreen,
      isInternalApiUrl: galleryState.isInternalApiUrl,
      isUpscaledUrl: galleryState.isUpscaledUrl,
      isUnmountedState,
      mountDownloadCodePage: page.mount,
      onPortraitLoad: galleryState.onPortraitLoad,
      onPortraitLoadError: galleryState.onPortraitLoadError,
      pageStatus: page.status,
      portraitCurrentUrlByUuid: galleryState.portraitCurrentUrlByUuid,
      portraits: galleryState.portraits,
      retryLoadingPortrait: galleryState.retryLoadingPortrait,
      retryDownloadCodePage: page.retry,
      retryExtras: digitalExtras.retry,
      sessionPass: page.sessionPass,
      unmountDownloadCodePage: page.unmount,
      updatePortrait: galleryState.updatePortrait,
    };
  },
  components: {
    CustomerDigitalExtras,
    CustomerDownloadLayout,
    CustomerDownloadStatusPanel,
    CustomerDownloadTabs,
    CustomerPortraitDownloadGallery,
    KioskLayout,
  },
  data() {
    return {
      PARTNER_LOGO,
      store,
      _portraitDelivery: null,
    };
  },
  computed: {
    customerDownloadActionFontScale() {
      return COMPACT_ACTION_LANGUAGES.has(
        shortenLocaleCode(store.currentLocale?.code)
      )
        ? 0.65
        : 0.75;
    },
    // A green-screen visitor with nothing printed still needs the QR ticket.
    showsSessionPass() {
      return this.pageStatus === 'ready' || this.pageStatus === 'pass-only';
    },
    hasPartialData() {
      return (
        this.brandingWarning ||
        this.portraits.some((portrait) => portrait.isError)
      );
    },
    _isUnmounted: {
      get() {
        return this.isUnmountedState;
      },
      set(value) {
        this.isUnmountedState = value;
      },
    },
  },
  created() {
    this._portraitDelivery = useCustomerPortraitDelivery({
      abortSignal: () => this.getDownloadAbortSignal(),
      getImageElement: (index) =>
        this.$refs.gallery?.getImageElement(index) ?? null,
      isInternalApiUrl: (url) => this.isInternalApiUrl(url),
      isUpscaledUrl: (url) => this.isUpscaledUrl(url),
      isUnmounted: () => this._isUnmounted,
      portraitCurrentUrlByUuid: () => this.portraitCurrentUrlByUuid,
      updatePortrait: (uuid, diff) => this.updatePortrait(uuid, diff),
    });
  },
  async mounted() {
    exposeToGlobalScope(this);
    await this.mountDownloadCodePage();
  },

  beforeUnmount() {
    this.unmountDownloadCodePage();
  },
  methods: {
    enterAnotherCode() {
      router.redirectToPath('/customer/portraits-download');
    },

    downloadPortrait(portrait) {
      return this._portraitDelivery.downloadPortrait(portrait);
    },

    sendOrSharePortrait(portrait, index) {
      const title = this.$t('CustomerPortraitsDownloadCode.shareTitle', {
        clientName: store.kiosk?.client_name || '',
      });
      const text = this.$t('CustomerPortraitsDownloadCode.shareText') || '';

      return this._portraitDelivery.sharePortrait(portrait, index, {
        title,
        text,
      });
    },
  },
};
</script>


<style lang="scss" scoped>
  .CustomerPortraitsDownloadCode {
    --Kiosk--wrapper-top-gap: var(--customer-download-wrapper-top-gap, 4rem);
    --Kiosk--wrapper-bottom-gap: var(--customer-download-wrapper-bottom-gap, 4rem);
    --Kiosk--wrapper-side-gap: var(--customer-download-wrapper-side-gap, 2rem);

    --CustomerPortraitsDownloadCode--mobile-logo-height: var(
      --customer-download-mobile-logo-height,
      calc(var(--Kiosk--wrapper-top-gap) * 0.5)
    );
    --CustomerPortraitsDownloadCode--mobile-logo-gap: var(
      --customer-download-mobile-logo-gap,
      calc(
        (
            var(--Kiosk--wrapper-top-gap) -
              var(--CustomerPortraitsDownloadCode--mobile-logo-height)
          ) /
          2
        )
    );
    min-height: 100vh;
    min-height: 100dvh;
  }

  .CustomerPortraitsDownloadCode :deep(.CustomerPortraitsDownloadCode__content) {
    display: grid;
    align-items: center;
    width: 100%;
    min-width: 0;
    margin-block: auto;
  }

  .CustomerPortraitsDownloadCode :deep(.KioskLayout__inner) {
    min-height: 100vh;
    min-height: 100dvh;
    overflow: visible;
  }

  .CustomerPortraitsDownloadCode__gallery-content {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-content: safe center;
    gap: clamp(1rem, 2vw, 1.5rem);
    width: 100%;
    min-width: 0;
  }

  .CustomerPortraitsDownloadCode--themed {
    --kiosk-shell-gap-top: 0px;
    --kiosk-shell-gap-inline: 0px;
    --kiosk-shell-gap-bottom: 0px;
  }

  .CustomerPortraitsDownloadCode--themed :deep(.CustomerPortraitsDownloadCode__content) {
    align-items: start;
    margin-block: 0;
    padding: 0;
  }

  .CustomerPortraitsDownloadCode--themed :deep(.KioskLayout__top-left),
  .CustomerPortraitsDownloadCode--themed :deep(.KioskLayout__top-right) {
    display: none;
  }

  .CustomerPortraitsDownloadCode__gallery-content :deep(.CustomerSessionPass),
  .CustomerPortraitsDownloadCode__gallery-content :deep(.CustomerDownloadStatus--compact) {
    margin-block: 0;
  }

  .CustomerPortraitsDownloadCode__partner {
    display: block;
    width: var(--customer-download-partner-width, var(--Kiosk--logo_size));
    height: var(--customer-download-partner-height, auto);
    margin: var(
      --customer-download-partner-margin,
      var(--Kiosk--logo_offset_y) var(--Kiosk--logo_offset_x) 0 0
    );
    transform: var(--customer-download-partner-transform, none);
    background: var(--Kiosk--font_color);
    mask: {
      image: url('../../assets/images/by-theartisan-citysaurus.svg');
      position: center;
      size: contain;
      repeat: no-repeat;
    }
  }

  .CustomerPortraitsDownloadCode__partner-image {
    display: block;
    opacity: 0;
    width: 100%;
  }
</style>
